import { useEffect, useState } from "react";
import { Plus, Trash2, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const CATEGORIES = ["Manasik", "Keberangkatan", "Kegiatan Jamaah", "Kajian", "Tanah Suci", "Kegiatan Sosial"];

export default function AdminGaleri() {
  const [albums, setAlbums] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [itemModal, setItemModal] = useState(null);
  const [form, setForm] = useState({ title: "", category: "Manasik", cover_image: "" });
  const [itemForm, setItemForm] = useState({ image_url: "", caption: "" });
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/galeri/albums`).then(({ data }) => setAlbums(data)).catch(() => setAlbums([]));
  useEffect(() => { load(); }, []);

  const handleCreateAlbum = async () => {
    try {
      await axios.post(`${API}/admin/galeri/albums`, form);
      toast.success("Album berhasil dibuat");
      setModalOpen(false);
      setForm({ title: "", category: "Manasik", cover_image: "" });
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleAddItem = async () => {
    try {
      await axios.post(`${API}/admin/galeri/items`, { ...itemForm, album_id: itemModal });
      toast.success("Foto berhasil ditambahkan");
      setItemModal(null);
      setItemForm({ image_url: "", caption: "" });
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/galeri/albums/${deleteId}`);
      toast.success("Album berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-galeri-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Galeri</h1>
          <p className="text-sm text-[#64748B]">Kelola album dan foto kegiatan.</p>
        </div>
        <Button data-testid="admin-galeri-add-album-button" onClick={() => setModalOpen(true)} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Album</Button>
      </div>

      {albums === null ? <LoadingScreen /> : albums.length === 0 ? (
        <EmptyState icon={Images} title="Belum ada album" description="Buat album pertama untuk mengelola galeri foto." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {albums.map((a) => (
            <div key={a.id} data-testid={`admin-galeri-album-${a.id}`} className="overflow-hidden rounded-xl border border-slate-100 bg-white">
              <img src={a.cover_image} alt={a.title} className="h-36 w-full object-cover" />
              <div className="p-4">
                <p className="font-medium text-[#1F2937]">{a.title}</p>
                <p className="text-xs text-[#64748B]">{a.category} &middot; {a.items?.length || 0} foto</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`admin-galeri-add-item-${a.id}`} onClick={() => setItemModal(a.id)} className="rounded-full">Tambah Foto</Button>
                  <Button size="sm" variant="ghost" data-testid={`admin-galeri-delete-album-${a.id}`} onClick={() => setDeleteId(a.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-galeri-album-modal">
          <DialogHeader><DialogTitle>Tambah Album</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul Album</Label><Input data-testid="album-form-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
            <div><Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="album-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>URL Gambar Sampul</Label><Input data-testid="album-form-cover" value={form.cover_image} onChange={(e) => setForm((f) => ({ ...f, cover_image: e.target.value }))} /></div>
            <Button data-testid="admin-galeri-album-submit" onClick={handleCreateAlbum} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan Album</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!itemModal} onOpenChange={(v) => !v && setItemModal(null)}>
        <DialogContent data-testid="admin-galeri-item-modal">
          <DialogHeader><DialogTitle>Tambah Foto</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>URL Gambar</Label><Input data-testid="gallery-item-form-url" value={itemForm.image_url} onChange={(e) => setItemForm((f) => ({ ...f, image_url: e.target.value }))} /></div>
            <div><Label>Keterangan</Label><Input data-testid="gallery-item-form-caption" value={itemForm.caption} onChange={(e) => setItemForm((f) => ({ ...f, caption: e.target.value }))} /></div>
            <Button data-testid="admin-galeri-item-submit" onClick={handleAddItem} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Tambah Foto</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Album?" description="Seluruh foto pada album ini akan ikut terhapus." onConfirm={handleDelete} testId="admin-galeri-delete-confirm" />
    </div>
  );
}
